import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import intersection from "lodash/intersection";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useRef } from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import type { Disposable } from "relay-runtime";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { articleSponsorType } from "shared/data/article";
import { section as sectionData } from "shared/data/section";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { useTracking } from "scmp-app/components/article/article-recirculation-widget/hooks";
import { checkIsPostMagazineArticle } from "scmp-app/components/article/article-render/post-magazine-article/helper";
import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import { appAtom } from "scmp-app/lib/app/atoms";
import type { mostReadWidgetArticle$key } from "scmp-app/queries/__generated__/mostReadWidgetArticle.graphql";
import type {
  mostReadWidgetQuery,
  mostReadWidgetQuery$variables,
} from "scmp-app/queries/__generated__/mostReadWidgetQuery.graphql";

import { Container, DisplayNumber, ItemContainer, StyledContentItem, Title } from "./styles";

const ypcSectionUuids = [
  sectionData.posties.entityUuid,
  sectionData.spark.entityUuid,
  sectionData.yp.entityUuid,
  sectionData.youngPost.entityUuid,
];

type Props = {
  className?: string;
  reference: mostReadWidgetArticle$key;
};

export const ArticleMostReadWidget: FunctionComponent<Props> = ({ className, reference }) => {
  const article = useFragment(
    graphql`
      fragment mostReadWidgetArticle on Article {
        entityId
      }
    `,
    reference,
  );

  const environment = useRelayEnvironment();
  const disposableReference = useRef<Disposable | null>(null);
  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);

  const [{ result, status }, { execute }] = useAsync(async () => {
    const query = graphql`
      query mostReadWidgetQuery($mostReadQueueName: String!) {
        mostReadQueue: queue(filter: { name: $mostReadQueueName }) {
          items(first: 10) {
            edges {
              node {
                ... on Content {
                  entityId
                  sectionsV2 {
                    value {
                      ... on Section {
                        entityUuid
                      }
                    }
                  }
                  ... on Article {
                    sponsorType
                  }
                  ...helpersCheckIsPostMagazineArticle
                  ...helpersCheckIsStyleArticle
                  ...homeSecondaryV2ContentItemContent
                }
              }
            }
          }
        }
      }
    `;
    const variables: mostReadWidgetQuery$variables = {
      mostReadQueueName:
        navigatorInfo?.country === "HK" ? "geo_most_popular_hk" : "geo_most_popular_global",
    };
    const queryRequest = getRequest(query);
    const queryDescriptor = createOperationDescriptor(queryRequest, variables);
    disposableReference.current = environment.retain(queryDescriptor);

    const data = await fetchQuery<mostReadWidgetQuery>(environment, query, variables, {
      fetchPolicy: "store-or-network",
    }).toPromise();

    return data;
  });

  useEffect(() => {
    if (status !== "not-executed" || !navigatorInfoLoaded) return;
    void execute();

    return () => {
      disposableReference.current?.dispose();
    };
  }, [execute, navigatorInfoLoaded, status]);

  const items = useMemo(
    () =>
      result?.mostReadQueue?.items?.edges
        .map(({ node }) => node)
        .filter(node => {
          const flattenSectionUuids =
            node.sectionsV2?.flatMap(
              section => section?.value?.flatMap(item => item?.entityUuid).filter(notEmpty) ?? [],
            ) ?? [];
          const hasYpcSection = intersection(ypcSectionUuids, flattenSectionUuids).length > 0;
          const isSponsorArticle = articleSponsorType.has(node.sponsorType ?? "");
          return (
            node.entityId !== article.entityId &&
            !hasYpcSection &&
            !isSponsorArticle &&
            !checkIsStyleArticle(node) &&
            !checkIsPostMagazineArticle(node)
          );
        })
        .slice(0, 3) ?? [],
    [article.entityId, result?.mostReadQueue?.items?.edges],
  );

  const { captureImpressionTrackingElement, handleClickTracking } = useTracking({
    getClickCustomizedParameters: (_entityId, urlAlias) => ({
      article_id: article.entityId,
      destination_url: getAbsoluteUrl(urlAlias),
      list_of_articles: items.map(({ entityId }) => entityId).join(","),
      page_type: "article",
      recommendation_id: "",
      widget_name: "most_read",
    }),
    getImpressionCustomizedParameters: () => ({
      list_of_articles: items.map(({ entityId }) => entityId).join(","),
      page_type: "article",
      widget_name: "most_read",
    }),
  });

  if (!notEmpty(items)) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "most_read",
        pgtype: "article",
      }}
    >
      <Container className={className}>
        <Title ref={captureImpressionTrackingElement}>Most Read</Title>
        {items.map((item, index) => (
          <ItemContainer key={item.entityId}>
            <DisplayNumber>{index + 1}</DisplayNumber>
            <StyledContentItem
              onClick={handleClickTracking}
              reference={item}
              withActionBar={false}
            />
          </ItemContainer>
        ))}
      </Container>
    </BaseLinkContextProvider>
  );
};

ArticleMostReadWidget.displayName = "ArticleMostReadWidget";
